import React, { useState } from 'react';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import { createGroupInviteLink } from '../../api/groupInviteLinkService';
import { buildJoinLeagueUrl } from '../../utils/joinLeagueIntent';

export default function GroupInviteLinkActions({
  groupId,
  groupName,
  copyLabel = 'Copy Invite Link',
  shareLabel = 'Share League',
  layout = 'row',
}) {
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const ensureInviteUrl = async () => {
    if (inviteUrl) return inviteUrl;
    setLoading(true);
    setError('');
    try {
      const data = await createGroupInviteLink(groupId);
      const url = buildJoinLeagueUrl(data.token);
      setInviteUrl(url);
      return url;
    } catch (err) {
      setError(err?.message || 'Could not create invite link');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      const url = await ensureInviteUrl();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      /* ensureInviteUrl sets error */
    }
  };

  const handleShare = async () => {
    try {
      const url = await ensureInviteUrl();
      if (typeof navigator.share === 'function') {
        await navigator.share({
          title: `Join ${groupName || 'my league'} on LockedIn`,
          text: 'Join our private league — tap the link to get in.',
          url,
        });
        return;
      }
      await handleCopy();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err?.message || 'Share failed');
      }
    }
  };

  const layoutClass = layout === 'column'
    ? 'flex flex-col gap-2'
    : 'flex flex-wrap gap-2';

  return (
    <div className="space-y-2">
      {error && <Alert tone="error" className="text-xs font-bold">{error}</Alert>}
      <div className={layoutClass}>
        <Button
          type="button"
          size="sm"
          variant="admin"
          disabled={loading}
          onClick={handleCopy}
        >
          {loading ? 'Preparing…' : copied ? 'Copied!' : copyLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={loading}
          onClick={handleShare}
        >
          {shareLabel}
        </Button>
      </div>
    </div>
  );
}
